import React, { useRef, useState } from "react";
import { Page } from "../Page";
import { api } from "../../api/api";
import { Company, EntityData, EntityType, Individual, JointHolding, Name, SMSF, TrackedItem, Trust, entity, form_def, getEntityTypeName } from "../../types";
import { useConditional } from "../../core/hooks/useConditional";
import { FormInline, FormModal } from "../../data-entry/FormModal";
import { newID } from "../../core/utils/unique";
import { useApi } from "../../core/hooks/useApi";
import { Table } from "../../core/ui/Table";
import { DateCol, EntityTypeCol, GenericCol, LastUpdatedCol, TagCol, TitleCol, VersionCol } from "../../layout/TableCells";
import { Modal } from "../../core/ui/Modal";
import { first, last } from "../../core/utils/array";
import { IndividualName } from "./Individual";
import { AddressCol, Addresses } from "./Address";
import { Tab, TabContainer } from "../../core/ui/Tab";
import { CompanyEdit } from "./Company";
import { SaveContext, requestSave, useSaveContext } from "../../core/hooks/useSaveContext";
import { broadcast } from "../../core/hooks/useChannel";
import { RelationshipsTabContent } from "./RelationshipsTabContent";
import { DocumentsTabContent } from "./DocumentsTabContent";
import { TrustEdit } from "./Trust";
import { JointHoldingEdit } from "./JointHolding";
import { SMSFEdit } from "./SMSF";


export function Entities(props: {

}) {
	const [token, setToken] = React.useState("");

	const createModal = useConditional(() => {
		return (
			<FormModal
				title="New Entity"
				form="entity-create"
				onCancel={createModal.close}
				onSave={(value: EntityData) => {

					const e = {
						title: `New ${getEntityTypeName(value.type)}`,
						deleted: 0,
						data: {
							type: value.type,
						},
					}

					api.entity_create(e).then(() => {
						setToken(newID());
						createModal.close();
					}).catch(() => {
	
					});	
				}}
			/>
		)
	});

	return (
		<Page
			compact={true}
			title={{
				text: "Entities",
				buttons: [
					{ title: "Create" }
				]
			}}
			onButton={(btn) => {
				createModal.show({});
			}}
		>
			<TabContainer
				key={token}
				displayStyle="border-indent"
			>
				<Tab title="Individuals" key="individual">
					<EntityList filterType="individual" />
				</Tab>
				<Tab title="Joint Holdings" key="joint">
					<EntityList filterType="joint" />
				</Tab>
				<Tab title="Australian Companies" key="company">
					<EntityList filterType="company" />
				</Tab>
				<Tab title="Foreign Companies" key="foreign-company">
					<EntityList filterType="foreign-company" />
				</Tab>
				<Tab title="Trusts" key="trust">
					<EntityList filterType="trust" />
				</Tab>
				<Tab title="SMSFs" key="smsf">
					<EntityList filterType="smsf" />
				</Tab>
			</TabContainer>

			{createModal.render()}

		</Page>
	);
}

export function EntityList(props: {
	filterType: EntityType,
}) {	
	
	const [token, setToken] = React.useState(newID());

	const entities = useApi({
		request: () => api.entity_search({ types: [props.filterType] }),

		prepareResult: (result: any[]) => {
			result.sort((a, b) => {
				return ((a && a.title) ? a.title : "").localeCompare((b && b.title) ? b.title : "");
			});

			return result;
		}
	}, [token]);
	
	const edit = useConditional((ent: entity) => {
		return (
			<EditEntity
				record={ent}
				onCancel={edit.close}
				onSave={() => {
					// update the title based on what the entity type is
					const type = ent.data.type;
					if (type === "individual") {
						const ind = (ent.data as Individual);
						if (ind.names.length > 0) ent.title = last(ind.names)!.display;
					
					} else if (type === "company") {
						const company = (ent.data as Company);
						if (company.names.length > 0) ent.title = last(company.names)!.name;
					
					} else if (type === "trust") {
						const trust = (ent.data as Trust);
						if (trust.names.length > 0) ent.title = last(trust.names)!.name;
					}

					api.entity_update(ent).then(() => {
						// console.dir(entities);
					}).catch(() => {

					})

					edit.close();
					setToken(newID());
				}}
				onDelete={() => {
					edit.close();

					api.entity_delete({ id: ent.id }).then(() => {
						setToken(newID());

					}).catch(() => {

					})
				}}
			/>
		)
	})

	const cols: any[] = [TitleCol()];

	if (props.filterType === "individual") {
		cols.push(AddressCol({
			className: "desktop-only",
			getAddress: (r, c) => {
				return r.address;
			},
			singleLine: true,
		}));
		cols.push(DateCol("Date of Birth", "data.dob"));

	} else if (props.filterType === "company") {
		cols.push(GenericCol("ACN", "data.acn"));
		cols.push(DateCol("Registered", "data.registered"));

	} else if (props.filterType === "trust") {
		cols.push(AddressCol({
			className: "desktop-only"
		}));
	}

	cols.push(LastUpdatedCol({ className: "desktop-only" }));

	return (
		<>
			<Table
				cols={cols}
				items={entities}
				interactive={true}
				onClick={(e, r) => {
					edit.show(r);
				}}
			/>
			{edit.render()}
		</>
	)
}

function EditEntity(props: {
	record: entity,
	onCancel: () => void,
	onSave: () => void,
	onDelete: () => void,
}) {
	const ent = props.record.data;

	let fields;
	if (ent.type === "individual") {
		const individual = ent as Individual;
		
		if (!individual.names) individual.names = [];
		if (!individual.addresses) individual.addresses = [];
		if (!individual.addressesPostal) individual.addressesPostal = [];

		fields = (
			<div className="flex vert align-start gap-10 grow-children">
				<IndividualName
					names={individual.names}
					large={true}
				/>
				<TabContainer displayStyle="border-indent">
					<Tab title="Details">
						<div className="flex vert align-stretch">
							<fieldset>
								<legend>Addresses</legend>
								<div className="flex horiz gap-10 align-start margin-bottom-10 grow-children space-evenly">
									<Addresses
										addressType="physical"
										addresses={individual.addresses}
									/>
									<Addresses
										addressType="postal"
										addresses={individual.addressesPostal}
									/>				
								</div>
							</fieldset>

							<fieldset>
								<legend>Birth details</legend>

								<FormInline
									form="entity-individual-edit"
									obj={individual}
								/>

							</fieldset>
						</div>
					</Tab>
					<Tab title="Relationships">
						<RelationshipsTabContent entity={props.record} />
					</Tab>
					<Tab title="Documents">
						<DocumentsTabContent />
					</Tab>
				</TabContainer>
			</div>
		);
	
	} else if (ent.type === "company") {
		const company = ent as Company;

		fields = (
			<CompanyEdit entity={props.record} />
		);

	} else if (ent.type === "trust") {
		const trust = ent as Trust;

		fields = (
			<TrustEdit trust={trust} />
		);

	} else if (ent.type === "joint") {
		const jh = ent as JointHolding;

		fields = (
			<JointHoldingEdit jh={jh} />
		)

	} else if (ent.type === "smsf") {
		const smsf = ent as SMSF;

		fields = (
			<SMSFEdit smsf={smsf} />
		);


	}

	const saveContext = useSaveContext();

	return (

		<SaveContext.Provider value={saveContext}>

			<Modal
				title={`Edit ${getEntityTypeName(ent.type)}`}

				size="width-75vw"

				onCancel={props.onCancel}
				onSave={() => {
					// check that everyone is happy with the save
					if (requestSave(saveContext.channel)) {
						props.onSave();						
					}
				}}
				onDelete={props.onDelete}
				showDelete={true}
			>
				{fields}
			</Modal>

		</SaveContext.Provider>

	)
}

